import React, { type ReactElement, useEffect } from 'react';
import {
	getErrorAttributes,
	isClientNetworkError,
	isHttpClientErrorResponse,
} from '@jira/assets-logging/src/common/utils.tsx';
import { GenericPermissionErrorPageAsync } from '@jira/platform__generic-permission-error-page/src/GenericPermissionErrorPageAsync.tsx';
import { NotFoundErrorPageAsync } from '@jira/platform__not-found-error-page/src/NotFoundErrorPageAsync.tsx';
import PageDI from '@atlaskit/page';
import {
	ContextualAnalyticsData,
	SCREEN,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import type { TrackerCallbacks } from '@atlassian/jira-common-experience-tracking-viewing/src/view/common/types.tsx';
import { ServiceDeskAnalyticsContext } from '@atlassian/jira-servicedesk-analytics/src/ui/servicedesk-analytics-context/index.tsx';
import {
	APP_NAMES,
	toPackageName,
	type AppName,
} from '@atlassian/jira-servicedesk-common/src/utils/app-names/index.tsx';

import { AsyncCmdbUpsellPage } from '@atlassian/jira-cmdb-upsell-page/src/async.tsx';
import { CmdbArchitectureContainer } from '@atlassian/jira-servicedesk-cmdb-architecture/src/ui/index.tsx';
import { isCMDBEnabledInEnvironment } from '@atlassian/jira-servicedesk-cmdb-fedramp/src/index.tsx';
import {
	useInsightVisibility,
	useWorkspaceContext,
} from '@atlassian/jira-servicedesk-insight-common-workspace-context/src/controllers/workspace-context/index.tsx';
import {
	getUserJsmLicenceState,
	isAssetsUser,
} from '@atlassian/jira-servicedesk-insight-common-workspace-context/src/services/resources/get-data/index.tsx';
import type { WorkspaceId } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/shared-types/index.tsx';
import type { PageWrapper } from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/page-style/index.tsx';
import { SubProductUpdater } from '@atlassian/jira-spa-apps-common/src/analytics-sub-product/sub-product-updater/index.tsx';
import { useAssetsTenantContext } from '@atlassian/jira-assets-tenant-context-provider/src/index.tsx';
import { Redirect } from '@atlassian/react-resource-router';
import { InsightGlobalConfigContainer } from '@atlassian/jira-servicedesk-insight-global-configuration-store/src/services/index.tsx';
import { useOrgId } from '@atlassian/jira-router-resources-navigation-org-id/src/index.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useAssetsDataManagerUserAuth } from '@atlassian/jira-assets-data-manager-common/src/hooks/assets-data-manager-user-auth/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { NoAccessErrorPage } from './no-access-error-page/index.tsx';
import { useHideAssetsReportsInIsolatedCloud } from './hooks/hide-assets-reports-ic-env/index.tsx';

/**
 * Page component will be deprecated very soon
 * Any new page should avoid using PageDI/PageWrapper wrapper if possible.
 */
const APP_NAMES_WITHOUT_WRAPPER: readonly AppName[] = [APP_NAMES.INSIGHT_LANDING_PAGE] as const;

const shouldSkipWrapper = (appName?: AppName): boolean => {
	if (!appName) {
		return false;
	}
	return APP_NAMES_WITHOUT_WRAPPER.includes(appName);
};

type LegacyProps = {
	children: (arg1: { workspaceId: WorkspaceId }) => ReactElement;
	appName?: AppName;
};

type Props = {
	appName: AppName;
	children: (arg1: { workspaceId: WorkspaceId }) => ReactElement;
	pageStyle?: PageWrapper;
	onExperienceFailure?: TrackerCallbacks['onExperienceFailure'];
	onExperienceAbort?: TrackerCallbacks['onExperienceAbort'];
};

export const AssetsAppBase = ({
	appName,
	children,
	pageStyle = PageDI,
	onExperienceFailure,
	onExperienceAbort,
}: Props): React.JSX.Element => (
	<>
		<SubProductUpdater subProduct="serviceDesk" />
		<ServiceDeskAnalyticsContext>
			<ContextualAnalyticsData sourceName={toPackageName(appName)} sourceType={SCREEN}>
				<AssetsAppBaseInternal
					appName={appName}
					pageStyle={pageStyle}
					onExperienceFailure={onExperienceFailure}
					onExperienceAbort={onExperienceAbort}
				>
					{children}
				</AssetsAppBaseInternal>
			</ContextualAnalyticsData>
		</ServiceDeskAnalyticsContext>
	</>
);

export const AssetsAppBaseInternal = ({
	appName,
	children,
	pageStyle: PageWrapper,
	onExperienceFailure,
	onExperienceAbort,
}: {
	appName: AppName;
	children: (arg1: { workspaceId: WorkspaceId }) => ReactElement;
	pageStyle: PageWrapper;
	onExperienceFailure?: TrackerCallbacks['onExperienceFailure'];
	onExperienceAbort?: TrackerCallbacks['onExperienceAbort'];
}): React.JSX.Element | null => {
	const { workspaceId, error, loading } = useWorkspaceContext();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const tenantContext = useAssetsTenantContext();
	const licenceState = getUserJsmLicenceState(tenantContext);
	const hasInsightPermission = useInsightVisibility();
	const userId = useAccountId();
	const { data: orgId } = useOrgId();
	const { isAssetsDataManagerUnauthorizedUser } = useAssetsDataManagerUserAuth();
	const shouldHideAssetsReports = useHideAssetsReportsInIsolatedCloud();

	useEffect(() => {
		if (onExperienceFailure && onExperienceAbort && fg('assets_sla_for_view_page_experiences')) {
			if (workspaceId === null && !loading && !error) {
				// eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
				onExperienceFailure(
					'assets-app-base',
					getErrorAttributes(new Error('Workspace ID is not set')),
				);
			} else if (error && !isClientNetworkError(error)) {
				const trackerFunction = isHttpClientErrorResponse(error)
					? onExperienceAbort
					: onExperienceFailure;
				trackerFunction?.('assets-app-base', getErrorAttributes(error));
			}
		}
	}, [onExperienceFailure, onExperienceAbort, workspaceId, loading, error]);

	if (!isCMDBEnabledInEnvironment()) {
		return <NotFoundErrorPageAsync />;
	}

	if (!licenceState.siteHasJsmInstalled || !userId) {
		return <Redirect to="/" />;
	}

	if (!licenceState.siteIsPremium) {
		return <AsyncCmdbUpsellPage />;
	}

	if (!licenceState.userLicensed) {
		return <NoAccessErrorPage />;
	}

	if (workspaceId == null) {
		return !loading && !error ? <NotFoundErrorPageAsync /> : null;
	}

	if (!hasInsightPermission || error) {
		return <GenericPermissionErrorPageAsync />;
	}

	if (isAssetsDataManagerUnauthorizedUser) {
		return <NoAccessErrorPage />;
	}

	if (shouldHideAssetsReports) {
		return <NotFoundErrorPageAsync />;
	}

	const shouldSkipPageWrapper = shouldSkipWrapper(appName) && fg('assets_common_schema_listing');

	const content = (
		<CmdbArchitectureContainer workspaceId={workspaceId}>
			{children({ workspaceId })}
		</CmdbArchitectureContainer>
	);

	return (
		<InsightGlobalConfigContainer
			isGlobal
			workspaceId={workspaceId}
			createAnalyticsEvent={createAnalyticsEvent}
			userId={userId}
			orgId={orgId}
		>
			{shouldSkipPageWrapper ? content : <PageWrapper>{content}</PageWrapper>}
		</InsightGlobalConfigContainer>
	);
};

// Clean this up while cleaning assets_as_an_app_v2 ff
export const InsightAppBaseV2 = ({ children, appName }: LegacyProps): React.JSX.Element | null => {
	const { workspaceId, error, loading } = useWorkspaceContext();
	const hasInsightPermission = useInsightVisibility();
	const tenantContext = useAssetsTenantContext();

	if (!isCMDBEnabledInEnvironment()) {
		return <NotFoundErrorPageAsync />;
	}

	if (!isAssetsUser(tenantContext)) {
		return <AsyncCmdbUpsellPage />;
	}

	if (workspaceId == null) {
		return !loading && !error ? <NotFoundErrorPageAsync /> : null;
	}

	if (hasInsightPermission === false || error) {
		return <GenericPermissionErrorPageAsync />;
	}

	const shouldSkipPageWrapper = shouldSkipWrapper(appName) && fg('assets_common_schema_listing');

	const content = (
		<CmdbArchitectureContainer workspaceId={workspaceId}>
			{children({ workspaceId })}
		</CmdbArchitectureContainer>
	);

	return shouldSkipPageWrapper ? content : <PageDI>{content}</PageDI>;
};
